import React, { useEffect, useState, useCallback } from "react";
import { 
  Grid, 
  Typography, 
  useTheme, 
  useMediaQuery, 
  Card, 
  CardContent, 
  CardMedia,
  Box
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 345,
  margin: '0 auto',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  background: 'white',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)',
  },
}));

const CarouselImage = styled(CardMedia)({
  height: 280,
  objectFit: 'contain',
  background: 'linear-gradient(to bottom, #f9f9f9, #ffffff)',
});

// Custom Carousel component
const Carousel = ({ images, productId, autoPlay = true, interval = 4000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  // Auto play functionality
  useEffect(() => {
    let timer;
    if (autoPlay && !isHovered && images.length > 1) {
      timer = setInterval(() => {
        nextSlide();
      }, interval);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [autoPlay, interval, isHovered, images.length, nextSlide]);

  return (
    <Box 
      sx={{ 
        position: 'relative',
        background: 'linear-gradient(to bottom, #f9f9f9, #ffffff)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: 280,
        }}
      >
        {images.map((imageUrl, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: index === currentIndex ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out',
            }}
          >
            <img
              src={imageUrl}
              alt={`Product image ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: '10px',
              }}
            />
          </Box>
        ))}
      </Box>
      
      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              borderRadius: '50%',
              cursor: 'pointer',
              zIndex: 1,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
              }
            }}
            onClick={prevSlide}
          >
            <KeyboardArrowLeft />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              borderRadius: '50%',
              cursor: 'pointer',
              zIndex: 1,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
              }
            }}
            onClick={nextSlide}
          >
            <KeyboardArrowRight />
          </Box>
        </>
      )}
      
      {/* Indicator dots */}
      {images.length > 1 && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            position: 'absolute',
            bottom: 5,
            width: '100%'
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mx: 0.5,
                bgcolor: index === currentIndex ? theme.palette.primary.main : 'rgba(0,0,0,0.2)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.2)',
                }
              }}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const ProductList = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const products = [
    { 
      id: 1, 
      title: 'Augumox cv 625',
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 2, 
      title: 'Augumox cv forte',
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 3, 
      title: 'Augumox cv forte 60ml',
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 4, 
      title: 'Pandop 40mg',
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 5, 
      title: 'Pandop DSR',
      images: [
        '/images/products/Pandop-dsr/Copy of DSC_4057.webp',
        '/images/products/Pandop-dsr/Copy of DSC_4058.webp',
        '/images/products/Pandop-dsr/Copy of DSC_4059.webp',
        '/images/products/Pandop-dsr/Copy of DSC_4062.webp',
        '/images/products/Pandop-dsr/Copy of DSC_4063.webp',
      ]
    },
    { 
      id: 6, 
      title: 'Montbery LC',
      description: 'Montelukast and levocetirizine combination',
      images: [
        '/images/products/Montebery-lc/Copy of DSC_4089.webp',
        '/images/products/Montebery-lc/Copy of DSC_4090.webp',
        '/images/products/Montebery-lc/Copy of DSC_4092.webp',
        '/images/products/Montebery-lc/Copy of DSC_4094.webp',
      ]
    },
    { 
      id: 7, 
      title: 'Montbery LC Kid',
      images: [
        '/images/products/Montbery-lckid/Copy of DSC_4125.webp',
        '/images/products/Montbery-lckid/Copy of DSC_4126.webp',
        '/images/products/Montbery-lckid/Copy of DSC_4127.webp',
        '/images/products/Montbery-lckid/Copy of DSC_4129.webp',
      ]
    },
    { 
      id: 8, 
      title: 'Vitamilin-AZ',
      images: [
        '/images/products/vitamilin-az/Copy of DSC_4070.webp',
        '/images/products/vitamilin-az/Copy of DSC_4074.webp',
        '/images/products/vitamilin-az/Copy of DSC_4075.webp',
        '/images/products/vitamilin-az/Copy of DSC_4076.webp',
      ]
    },
    { 
      id: 9, 
      title: 'Mefbery P',
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 10, 
      title: 'Berymol ASP',
      images: [
        '/images/products/Berymol-asp/Copy of DSC_4113.webp',
        '/images/products/Berymol-asp/Copy of DSC_4114.webp',
        '/images/products/Berymol-asp/Copy of DSC_4116.webp',
        '/images/products/Berymol-asp/Copy of DSC_4117.webp',
      ]
    },
    { 
      id: 11, 
      title: 'Berymol 250',
      images: [
       '/images/medicine.jpg',
      ]
    },
    { 
      id: 12, 
      title: 'Zincbery',
      images: [
        '/images/products/Zincbery/Copy of DSC_4134.webp',
        '/images/products/Zincbery/Copy of DSC_4135.webp',
        '/images/products/Zincbery/Copy of DSC_4137.webp',
      ]
    },
    { 
      id: 13, 
      title: 'Amroline Plus', 
      description: 'Advanced respiratory care formula',
      images: [
        '/images/products/Amroline-plus/Copy of DSC_4149.webp',
        '/images/products/Amroline-plus/Copy of DSC_4150.webp',
        '/images/products/Amroline-plus/Copy of DSC_4151.webp',
        '/images/products/Amroline-plus/Copy of DSC_4152.webp',
      ]
    },
    { 
      id: 14, 
      title: 'Amroline LS', 
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 15, 
      title: 'Amroline XP', 
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 16, 
      title: 'Pulmobery', 
      images: [
        '/images/medicine.jpg',
      ]
    },
    { 
      id: 17, 
      title: 'Berysod', 
      images: [
        '/images/products/Berysod/Copy of DSC_4146.webp',
        '/images/products/Berysod/Copy of DSC_4147.webp',
        '/images/products/Berysod/Copy of DSC_4148.webp',
      ]
    },
    { 
      id: 18, 
      title: 'Calciolin', 
      images: [
        '/images/products/Calciolin/Copy of DSC_4118.webp',
        '/images/products/Calciolin/Copy of DSC_4119.webp',
        '/images/products/Calciolin/Copy of DSC_4120.webp',
        '/images/products/Calciolin/Copy of DSC_4121.webp',
        '/images/products/Calciolin/Copy of DSC_4122.webp',
      ]
    },
    { 
      id: 19, 
      title: 'Lanzbery 15mg', 
      images: [
        '/images/products/Lanzbery/Copy of DSC_4064.webp',
        '/images/products/Lanzbery/Copy of DSC_4066.webp',
        '/images/products/Lanzbery/Copy of DSC_4067.webp',
        '/images/products/Lanzbery/Copy of DSC_4068.webp',
      ]
    },
    { 
      id: 20, 
      title: 'Rabilight O', 
      images: [
        '/images/products/Rabilight-O/Copy of DSC_4077.webp',
        '/images/products/Rabilight-O/Copy of DSC_4078.webp',
        '/images/products/Rabilight-O/Copy of DSC_4079.webp',
        '/images/products/Rabilight-O/Copy of DSC_4081.webp',
      ]
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <img
          src="/images/bf1.jpg"
          alt="Header"
          style={{
            width: '100%', 
            height: isSmallScreen ? 'auto' : '70vh',
            objectFit: isSmallScreen ? 'contain' : 'cover',
            marginBottom: '10px',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography 
          sx={{ 
            textAlign: 'center', 
            fontSize: 30, 
            fontWeight: 'bold', 
            mb: 5,
            mt: 3
          }}
        >
          PRODUCTS
        </Typography>
      </Grid>
      
      <Grid container spacing={4} sx={{ px: isSmallScreen ? 2 : 5, mb: 5 }}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <StyledCard>
              {product.images && product.images.length > 0 ? (
                <Carousel
                  images={product.images}
                  productId={product.id}
                  autoPlay={true}
                  interval={4000}
                />
              ) : (
                <CarouselImage
                  component="img"
                  image="/images/placeholder-product.jpg"
                  alt={product.title}
                />
              )}
              <CardContent sx={{ 
                p: 2.5, 
                '&:last-child': { 
                  paddingBottom: 2.5,
                },
                borderTop: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                <Typography 
                  gutterBottom 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    fontWeight: '600',
                    fontSize: '1rem',
                    color: '#222',
                    letterSpacing: '0.02em',
                  }}
                >
                  {product.title}
                </Typography>
                {product.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.85rem',
                      mt: 0.5 
                    }}
                  >
                    {product.description}
                  </Typography>
                )}
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default ProductList;